import React, { useState, useEffect } from 'react';
import { Box, Drawer, List, ListItem, ListItemText, ListItemIcon, Typography, createTheme, ThemeProvider, CssBaseline, AppBar, Toolbar, Avatar } from '@mui/material';
// axios removed as it is unused
import AdminOrders from '../components/AdminOrders';
import AdminCakes from '../components/AdminCakes';
import User from '../data/User';
import AdminReviewPage from './Review/ReviewsManagement';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import CakeIcon from '@mui/icons-material/Cake';
import StarIcon from '@mui/icons-material/Star';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';

const defaultTheme = createTheme({
  palette: {
    primary: {
      main: '#e88ba9', // Soft pastel pink
    },
    background: {
      default: '#fffafb', // Off-white with a hint of pink
      paper: '#ffffff',
    },
    text: {
      primary: '#5c434a',
      secondary: '#8c7077',
    }
  },
  typography: {
    fontFamily: '"Outfit", "Inter", sans-serif',
    h5: {
      fontFamily: '"Playfair Display", serif',
      fontWeight: 700,
      color: '#d46a89',
    }
  },
});

const drawerWidth = 260;

const AdminDashboard = () => {
  const [selectedPane, setSelectedPane] = useState('orders');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const fetchUser = async () => {
    const userInstance = User.instance;
    await userInstance.fetchData();
    setUser(userInstance);
  };

  useEffect(() => {
    fetchUser();
  }, [selectedPane]);

  const handleLogout = () => {
    User.instance.signOut();
    navigate('/');
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fffafb' }}>
        <Typography variant="h6" sx={{ color: '#e88ba9' }}>Welcome to the Bakery...</Typography>
      </Box>
    );
  }

  if (user.role !== 'admin') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fffafb' }}>
        <Typography variant="h5" color="error">
          Oops! You need to sign in as an admin to view this kitchen.
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex', backgroundColor: '#fffafb', minHeight: '100vh' }}>
        <CssBaseline />

        {/* Top App Bar */}
        <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: '#fff', boxShadow: '0 2px 10px rgba(232, 139, 169, 0.1)' }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DashboardIcon sx={{ color: '#e88ba9', mr: 2 }} />
              <Typography variant="h5" noWrap sx={{ mt: 1 }}>
                Sweet Dashboard
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, color: '#8c7077', fontWeight: 600 }}>
                {user.name}
              </Typography>
              <Avatar sx={{ bgcolor: '#e88ba9' }}>{user.name?.charAt(0)}</Avatar>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: '#fff',
              borderRight: '1px solid rgba(232, 139, 169, 0.2)',
              boxShadow: '4px 0 20px rgba(0,0,0,0.02)'
            },
          }}
        >
          <Toolbar /> {/* Spacer for AppBar */}
          <Box sx={{ overflow: 'auto', mt: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <List sx={{ px: 2 }}>
              <ListItem
                button
                onClick={() => setSelectedPane('orders')}
                sx={{
                  borderRadius: '12px', mb: 1,
                  backgroundColor: selectedPane === 'orders' ? '#fcf4f4' : 'transparent',
                  color: selectedPane === 'orders' ? '#d46a89' : '#8c7077',
                  '&:hover': { backgroundColor: '#fcf4f4', color: '#d46a89' }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}><LocalMallIcon /></ListItemIcon>
                <ListItemText primary="Manage Orders" primaryTypographyProps={{ fontWeight: selectedPane === 'orders' ? 700 : 500 }} />
              </ListItem>

              <ListItem
                button
                onClick={() => setSelectedPane('cakes')}
                sx={{
                  borderRadius: '12px', mb: 1,
                  backgroundColor: selectedPane === 'cakes' ? '#fcf4f4' : 'transparent',
                  color: selectedPane === 'cakes' ? '#d46a89' : '#8c7077',
                  '&:hover': { backgroundColor: '#fcf4f4', color: '#d46a89' }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}><CakeIcon /></ListItemIcon>
                <ListItemText primary="Manage Cakes" primaryTypographyProps={{ fontWeight: selectedPane === 'cakes' ? 700 : 500 }} />
              </ListItem>

              <ListItem
                button
                onClick={() => setSelectedPane('reviews')}
                sx={{
                  borderRadius: '12px', mb: 1,
                  backgroundColor: selectedPane === 'reviews' ? '#fcf4f4' : 'transparent',
                  color: selectedPane === 'reviews' ? '#d46a89' : '#8c7077',
                  '&:hover': { backgroundColor: '#fcf4f4', color: '#d46a89' }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}><StarIcon /></ListItemIcon>
                <ListItemText primary="Manage Reviews" primaryTypographyProps={{ fontWeight: selectedPane === 'reviews' ? 700 : 500 }} />
              </ListItem>
            </List>

            <Box sx={{ flexGrow: 1 }} />

            <List sx={{ px: 2, mb: 2 }}>
              <ListItem
                button
                onClick={handleLogout}
                sx={{
                  borderRadius: '12px',
                  color: '#8c7077',
                  '&:hover': { backgroundColor: '#fff0f0', color: '#e53935' }
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}><LogoutIcon /></ListItemIcon>
                <ListItemText primary="Sign Out" primaryTypographyProps={{ fontWeight: 500 }} />
              </ListItem>
            </List>
          </Box>
        </Drawer>

        {/* Main Content Area */}
        <Box component="main" sx={{ flexGrow: 1, p: 4, mt: 8 }}>
          {selectedPane === 'orders' && <AdminOrders />}
          {selectedPane === 'cakes' && <AdminCakes />}
          {selectedPane === 'reviews' && <AdminReviewPage />}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AdminDashboard;
